import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { Container, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';


// --- Firebase Initialization ---
// eslint-disable-next-line no-undef
const firebaseConfig = JSON.parse(typeof __firebase_config !== 'undefined' ? __firebase_config : '{}');
let auth;
try {
  // 既に初期化されている場合は既存のアプリを使用、なければ初期化
  const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
  auth = getAuth(app);
} catch (e) {
  console.warn("Firebase init check:", e);
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function PasswordReset() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oobCode, setOobCode] = useState(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const query = useQuery();
  const navigate = useNavigate();

  // パスワード要件: 8文字以上、大文字・小文字・数字・記号を含む
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!-\/:-@\[-`{-~]).{8,}$/;

  useEffect(() => {
    const code = query.get('oobCode');
    if (!code) {
      // 開発中の確認用に、コードがなくても画面を表示させたい場合はここを調整してください
      // 今回はエラーを表示せずにローディングを終了します
      setIsLoading(false);
      return;
    }
    setOobCode(code);

    if (auth) {
      verifyPasswordResetCode(auth, code)
        .then((email) => {
          setEmail(email);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setError('リンクが無効か、期限切れです。');
          setIsLoading(false);
        });
    }
  }, [query]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordRegex.test(password)) {
      setError('パスワードの要件を満たしていません。');
      return;
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return;
    }
    if (!oobCode) {
      setError('リセットコードが見つかりません。');
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error(err);
      setError('パスワードの設定に失敗しました。');
    }
  };

  if (isLoading) {
    return <Container className="mt-5 text-center"><Spinner animation="border" /></Container>;
  }

  if (success) {
    return (
      <Container className="mt-5">
        <Alert variant="success">
          パスワードが正常に設定されました。<br />
          ログイン画面へ移動します...
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="justify-content-md-center">
        <Col md={8} lg={6}>
          <h2 className="mb-4">パスワード設定</h2>
          
          {email && <p className="mb-3">アカウント: {email}</p>}
          
          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>新しいパスワード</Form.Label>
              <Form.Control
                type="password"
                placeholder="新しいパスワードを入力"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                isInvalid={password.length > 0 && !passwordRegex.test(password)}
                isValid={passwordRegex.test(password)}
              />
              <Form.Text className="text-muted">
                <div style={{ fontSize: '0.85rem', marginTop: '5px' }}>
                  <strong>【パスワード要件】</strong><br />
                  8文字以上、以下をすべて含む：<br />
                  <span className={password.match(/[A-Z]/) ? "text-success" : ""}>✔ 英大文字</span>{' '}
                  <span className={password.match(/[a-z]/) ? "text-success" : ""}>✔ 英小文字</span>{' '}
                  <span className={password.match(/[0-9]/) ? "text-success" : ""}>✔ 数字</span>{' '}
                  <span className={password.match(/[!-\/:-@\[-`{-~]/) ? "text-success" : ""}>✔ 記号</span>
                </div>
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>パスワード（確認用）</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                isInvalid={confirmPassword.length > 0 && password !== confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                一致しません。
              </Form.Control.Feedback>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={!passwordRegex.test(password) || password !== confirmPassword}
              >
                設定してログインへ
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default PasswordReset;